import { useCallback, useEffect, useState } from "react";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { getPushConfig, savePushPreferences, sendTestPush } from "../../lib/api";
import {
  currentSubscription, disablePush, enablePush, isIos, isStandalone, pushSupported, syncPushSubscription,
} from "../../lib/push";

// Sidebar entry + dialog: turn phone/desktop notifications on for this device
// and choose which events push. Backend: routes/push.py, services/web_push.py.
export default function NotificationsButton() {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [onThisDevice, setOnThisDevice] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { syncPushSubscription(); }, []);

  const load = useCallback(async () => {
    try {
      const [{ data }, sub] = await Promise.all([getPushConfig(), currentSubscription()]);
      setConfig(data);
      setOnThisDevice(Boolean(sub) && typeof Notification !== "undefined" && Notification.permission === "granted");
    } catch {
      setConfig(null);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const toggleEvent = async (key, value) => {
    const prev = config.preferences;
    setConfig({ ...config, preferences: { ...prev, [key]: value } });
    try {
      const { data } = await savePushPreferences({ [key]: value });
      setConfig((c) => ({ ...c, preferences: data.preferences }));
    } catch {
      setConfig((c) => ({ ...c, preferences: prev }));
      toast.error("Could not save that switch");
    }
  };

  const needsHomeScreen = isIos() && !isStandalone();
  const supported = pushSupported();
  const Icon = onThisDevice ? BellRing : Bell;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full mb-2"
        data-testid="notifications-btn"
      >
        <Icon className="w-4 h-4" />
        <span>Notifications</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="notifications-dialog">
          <DialogHeader>
            <DialogTitle>Notifications</DialogTitle>
            <DialogDescription>
              Push alerts to this phone or computer, even when BR OS is closed.
            </DialogDescription>
          </DialogHeader>

          {needsHomeScreen ? (
            <p className="text-sm text-muted-foreground" data-testid="notifications-ios-hint">
              On iPhone, notifications only work from the Home Screen app: tap Share, then Add to Home Screen,
              open BR OS from the new icon and come back here. Needs iOS 16.4 or later.
            </p>
          ) : !supported ? (
            <p className="text-sm text-muted-foreground">This browser cannot receive notifications.</p>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3">
              <div className="text-sm">
                <p className="font-medium text-foreground">{onThisDevice ? "On for this device" : "Off for this device"}</p>
                <p className="text-xs text-muted-foreground">
                  {config ? `${config.devices} device${config.devices === 1 ? "" : "s"} on your account` : ""}
                </p>
              </div>
              {onThisDevice ? (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => run(sendTestPush, "Test sent")} data-testid="notifications-test">
                    Send test
                  </Button>
                  <Button size="sm" variant="ghost" disabled={busy}
                    onClick={() => run(disablePush, "Notifications off for this device")}>
                    Turn off
                  </Button>
                </div>
              ) : (
                <Button size="sm" disabled={busy}
                  onClick={() => run(enablePush, "Notifications on for this device")} data-testid="notifications-enable">
                  Enable
                </Button>
              )}
            </div>
          )}

          {config && (
            <div className="space-y-2" data-testid="notifications-events">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Notify me about</p>
              {config.events.map((ev) => (
                <label key={ev.key} className="flex items-center justify-between gap-3 text-sm py-1">
                  <span>{ev.label}</span>
                  <Switch
                    checked={Boolean(config.preferences?.[ev.key])}
                    onCheckedChange={(v) => toggleEvent(ev.key, v)}
                    data-testid={`notifications-event-${ev.key}`}
                  />
                </label>
              ))}
              <p className="text-xs text-muted-foreground pt-1">
                You only get alerts for clients and records you can see in BR OS.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
